/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { formatRecordingDateTime } from "./utils/recordingDate";
import React, { useState, useEffect, useRef, useMemo } from "react";
// ❗️ Удален импорт ReactMarkdown, так как он больше не нужен
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "";
const AI_ENABLED = import.meta.env.VITE_ENABLE_AI === "true";
import { useClientLegacy } from "./ClientContext";
import { useClient } from "./ClientContext";
import { InputField } from "./input/Input";
import { copyShareLink } from "./utils/copyShareLink";
import { localizedRequestError } from "./utils/localizedError";
import { backendFetch, recordingShare } from "./utils/backend";
import styles from "./RecordPage.module.css";
import { usePageTitle } from "./usePageTitle";
import { RecordingAudioPlayer } from "./components/RecordingAudioPlayer";

// ❗️ ОБНОВЛЕНО: Интерфейс теперь ожидает структурированный JSON-объект, а не строку
interface ParsedSummary {
  summary?: string;
  highlights?: string[];
  decisions?: string;
  actionItems?: string;
}

interface SummaryData {
  summary: ParsedSummary | null; // Теперь это объект
  transcript: any[];
  recordingStartTime?: number;
  mediaUrl?: string | null;
  mediaStartedAt?: number | null;
  type: "video" | "audio" | "text";
  mediaFile: string | null;
  date?: number;
  roomName?: string | null;
  participants?: { name: string }[];
  sizeBytes?: number;
  canShare?: boolean;
}

export const RecordPage: React.FC = () => {
  const location = useLocation();
  const { client } = useClient();
  const { passwordlessUser } = useClientLegacy();
  const { t } = useTranslation();

  const uiText = useMemo(
    () => ({
      notFound: t("instance.record_page.notFound"),
      defaultTitle: t("instance.record_page.defaultTitle"),
      recordingFrom: t("instance.record_page.recordingFrom"),
      processingMedia: t("instance.record_page.processingMedia"),
      hideSummary: t("instance.record_page.hideSummary"),
      showSummary: t("instance.record_page.showSummary"),
      download: t("instance.record_page.download"),
      textSession: t("instance.record_page.textSession"),
      loadingData: t("instance.record_page.loadingData"),
      fullTranscript: t("instance.record_page.fullTranscript"),
      noTextData: t("instance.record_page.noTextData"),
      summaryHeaders: {
        summary: t("instance.record_page.summaryHeaders_summary"),
        highlights: t("instance.record_page.summaryHeaders_highlights"),
        decisions: t("instance.record_page.summaryHeaders_decisions"),
        actionItems: t("instance.record_page.summaryHeaders_actionItems"),
      },
    }),
    [t],
  );

  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [sharedLink, setSharedLink] = useState("");
  const [shareError, setShareError] = useState("");
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFallback, setCopyFallback] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timeout = window.setTimeout(() => setCopied(false), 5000);
    return () => window.clearTimeout(timeout);
  }, [copied]);
  const [currentTime, setCurrentTime] = useState(0);
  const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement | any>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const rawVideoId = urlParams.get("v");
  const shareToken = urlParams.get("share");
  const recordingId = rawVideoId
    ? rawVideoId.replace(/\.(mp4|webm|mp3|ogg)$/, "")
    : null;

  useEffect(() => {
    if (!recordingId || recordingId.includes("/") || recordingId.includes("\\"))
      return;
    setLoadingAI(true);
    setSummaryData(null);
    setSharedLink("");
    setShareError("");
    setCopied(false);
    setCopyFallback(false);
    backendFetch(
      client,
      `/api/recordings/summary/${encodeURIComponent(recordingId)}${shareToken ? `?share=${encodeURIComponent(shareToken)}` : ""}`,
    )
      .then((res) => {
        if (!res.ok) throw new Error("No data");
        return res.json();
      })
      .then((data: SummaryData) => {
        setSummaryData(data);
        if (AI_ENABLED && data.type === "text") {
          setShowSummary(true);
        }
      })
      .catch(() => setSummaryData(null))
      .finally(() => setLoadingAI(false));
  }, [recordingId, shareToken, client]);

  const recordingDate = summaryData?.mediaStartedAt || summaryData?.recordingStartTime || summaryData?.date;
  const recordingTitle = recordingDate
    ? `${uiText.recordingFrom} ${formatRecordingDateTime(recordingDate)}`
    : uiText.defaultTitle;
  usePageTitle(recordingTitle);
  const recordingInfo = summaryData ? (
    <div className={styles.recordingInfo}>
      {summaryData.roomName && <p className={styles.roomName}>{summaryData.roomName}</p>}
      {recordingDate && <time dateTime={new Date(recordingDate).toISOString()}>{formatRecordingDateTime(recordingDate)}</time>}
      <p>{t("instance.ui.recording_participants")}: {summaryData.participants?.length
        ? summaryData.participants.map(p => p.name).join(", ")
        : t("instance.ui.recording_participants_unknown")}</p>
    </div>
  ) : null;

  if (!recordingId || recordingId.includes("/") || recordingId.includes("\\")) {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <h2 style={{ color: "var(--app-text)", fontFamily: "sans-serif" }}>
          {uiText.notFound}
        </h2>
      </div>
    );
  }

  const handleTimeUpdate = () => {
    if (mediaRef.current) {
      setCurrentTime(mediaRef.current.currentTime);
    }
  };

  const handleSeek = (timeToSeek: number) => {
    if (isNaN(timeToSeek) || !mediaRef.current) return;
    mediaRef.current.currentTime = timeToSeek;
    mediaRef.current
      .play()
      .catch((e: any) => console.log("Autoplay blocked:", e));
  };

  const title = uiText.defaultTitle;

  const isTextOnly = summaryData?.type === "text";
  const mediaFilename =
    summaryData?.mediaFile ||
    (rawVideoId && /\.(mp4|webm|mp3|ogg)$/.test(rawVideoId)
      ? rawVideoId
      : null);
  const hasMedia = !!mediaFilename;
  const fileUrl =
    hasMedia && summaryData?.mediaUrl
      ? `${BACKEND_URL}${summaryData.mediaUrl}`
      : "";
  const isAudio =
    summaryData?.type === "audio" ||
    (mediaFilename
      ? mediaFilename.endsWith(".mp3") || mediaFilename.endsWith(".ogg")
      : false);

  const canShare = !!(
    summaryData &&
    (shareToken || (client && !passwordlessUser && !client.isGuest()))
  );
  const sharingAllowed = Boolean(shareToken || summaryData?.canShare);
  const shareButton = canShare ? (
    <button
      type="button"
      className={`glass-button ${styles.actionButton}`}
      aria-label={t("instance.ui.share_recording")}
      disabled={sharing || !sharingAllowed}
      title={
        !sharingAllowed
          ? t("instance.ui.recording_share_unavailable")
          : undefined
      }
      onClick={() => {
        setSharing(true);
        setShareError("");
        setCopied(false);
        setCopyFallback(false);
        const link = (
          shareToken
            ? Promise.resolve(
                new URL(
                  `/record?${new URLSearchParams({ v: recordingId!, share: shareToken })}`,
                  window.location.origin,
                ).href,
              )
            : sharedLink
              ? Promise.resolve(sharedLink)
              : recordingShare(client, recordingId)
        ).then((value) => {
          setSharedLink(value);
          return value;
        });
        // Initiate write synchronously; Safari requires this click activation.
        void copyShareLink(link)
          .then(() => setCopied(true))
          .catch(async () => {
            try {
              await link;
              setCopyFallback(true);
            } catch (error) {
              setShareError(localizedRequestError(error, t));
            }
          })
          .finally(() => setSharing(false));
      }}
    >
      <span className={styles.shareLabel}>
        <span className={styles.shareLabelSizer} aria-hidden="true">{t("instance.ui.link_copied")}</span>
        <span key={String(copied)} className={styles.shareLabelText}>{copied ? t("instance.ui.link_copied") : t("instance.ui.share")}</span>
      </span>
    </button>
  ) : null;
  const shareFeedback =
    canShare && (copyFallback || shareError) ? (
      <div className={styles.shareFeedback}>
        {copyFallback && (
          <p role="alert">{t("instance.ui.copy_link_fallback")}</p>
        )}
        {copyFallback && sharedLink && (
          <InputField
            type="text"
            label={t("instance.ui.recording_link")}
            value={sharedLink}
            readOnly
          />
        )}
        {shareError && <p role="alert">{shareError}</p>}
      </div>
    ) : null;

  // Компенсация холодного старта Egress (в секундах)
  const EGRESS_BOOT_DELAY = 0;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        bottom: "auto",
        minHeight: "100svh",
        overflowY: "visible",
        background: "transparent",
        color: "var(--app-text)",
        fontFamily: "sans-serif",
        boxSizing: "border-box",
        padding: "80px 20px 40px",
      }}
    >
      {((client && !passwordlessUser && !client.isGuest()) ||
        location.state?.fromCallList) && (
        <div style={{ maxWidth: "1000px", margin: "0 auto 20px" }}>
          <Link
            className="glass-button header-action"
            to={import.meta.env.VITE_ADMIN_URL || "/"}
          >
            {t("instance.record_page.back_to_calls")}
          </Link>
        </div>
      )}
      {isTextOnly && canShare && (
        <section className={styles.textShare}>
          {recordingInfo}
          {shareButton}
          {shareFeedback}
        </section>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          minHeight: "min-content",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "20px",
            width: "100%",
            maxWidth: isTextOnly ? "800px" : showSummary ? "1400px" : "1000px",
            transition: "max-width 0.3s ease",
            flexDirection: isTextOnly ? "column" : "row",
            margin: "0 auto",
          }}
        >
          {!isTextOnly && (
            <div
              style={{
                flex: 1,
                minWidth: 0,
                boxSizing: "border-box",
                background: "var(--app-panel)",
                backdropFilter: "blur(10px)",
                padding: "20px",
                borderRadius: "12px",
                height: "fit-content",
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                border: "1px solid var(--app-border)",
              }}
            >
              <div
                style={{
                  marginBottom: "12px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <h2
                  style={{
                    color: "var(--app-text)",
                    margin: 0,
                    fontFamily: "sans-serif",
                  }}
                >
                  {title}
                </h2>
                {summaryData?.sizeBytes !== undefined && (
                  <span style={{ color: "var(--app-muted)", fontSize: 13 }}>
                    {(summaryData.sizeBytes / (1024 * 1024)).toFixed(1)} MB
                  </span>
                )}

                <div className={styles.actions}>
                  {AI_ENABLED && (
                    <button
                      onClick={() => setShowSummary(!showSummary)}
                      style={{
                        background: "var(--glass-fill)",
                        border: "1px solid var(--app-border)",
                        color: "var(--app-text)",
                        padding: "10px 20px",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "0.2s",
                        fontFamily: "sans-serif",
                      }}
                    >
                      {showSummary ? uiText.hideSummary : uiText.showSummary}
                    </button>
                  )}

                  {shareButton}
                  {hasMedia && (
                    <a
                      className={`glass-button ${styles.actionButton}`}
                      href={`${fileUrl}${fileUrl.includes("?") ? "&" : "?"}download=1`}
                      download
                    >
                      {uiText.download}
                    </a>
                  )}
                </div>
              </div>
              {shareFeedback}
              {recordingInfo}
              {hasMedia && isAudio ? (
                <RecordingAudioPlayer
                  key={fileUrl}
                  ref={mediaRef}
                  src={fileUrl}
                  onTimeUpdate={handleTimeUpdate}
                />
              ) : hasMedia ? (
                <video
                  ref={mediaRef}
                  src={fileUrl}
                  controls
                  controlsList="nodownload"
                  onTimeUpdate={handleTimeUpdate}
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    background: "black",
                    objectFit: "cover",
                    maxHeight: "600px",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "black",
                    color: "gray",
                    borderRadius: "8px",
                  }}
                >
                  {uiText.processingMedia}
                </div>
              )}


            </div>
          )}

          {AI_ENABLED && showSummary && (
            <div
              style={{
                width: isTextOnly ? "100%" : "400px",
                background: "var(--app-panel)",
                backdropFilter: "blur(10px)",
                border: "1px solid var(--app-border)",
                borderRadius: "12px",
                padding: "20px",
                color: "var(--app-text)",
                fontFamily: "sans-serif",
                maxHeight: "calc(100vh - 120px)",
                overflowY: "auto",
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                animation: "fadeIn 0.3s ease",
              }}
            >
              {isTextOnly && (
                <div
                  style={{
                    borderBottom: "1px solid var(--app-border)",
                    paddingBottom: "15px",
                    marginBottom: "20px",
                  }}
                >
                  <h2 style={{ margin: 0 }}>📄 {title}</h2>
                  <span style={{ color: "var(--app-muted)", fontSize: "14px" }}>
                    {uiText.textSession}
                  </span>
                </div>
              )}

              {loadingAI ? (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "50px",
                    color: "var(--app-subtle)",
                  }}
                >
                  {uiText.loadingData}
                </div>
              ) : summaryData ? (
                <>
                  {/* ❗️ ИСПРАВЛЕННЫЙ РЕНДЕР: Используем чистый JSX вместо ReactMarkdown */}
                  {summaryData.summary && (
                    <div
                      className="summary-container"
                      style={{
                        background: "rgba(0,0,0,0.2)",
                        padding: "15px",
                        borderRadius: "8px",
                        marginBottom: "20px",
                        lineHeight: "1.5",
                      }}
                    >
                      {summaryData.summary.summary && (
                        <>
                          <h3 style={{ marginTop: 0 }}>
                            {uiText.summaryHeaders.summary}
                          </h3>
                          <p>{summaryData.summary.summary}</p>
                        </>
                      )}

                      {summaryData.summary.highlights &&
                        summaryData.summary.highlights.length > 0 && (
                          <>
                            <h3>{uiText.summaryHeaders.highlights}</h3>
                            <ul
                              style={{ paddingLeft: "20px", margin: "10px 0" }}
                            >
                              {summaryData.summary.highlights.map((h, i) => (
                                <li key={i} style={{ marginBottom: "5px" }}>
                                  {h}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}

                      {summaryData.summary.decisions && (
                        <>
                          <h3>{uiText.summaryHeaders.decisions}</h3>
                          <p>{summaryData.summary.decisions}</p>
                        </>
                      )}

                      {summaryData.summary.actionItems && (
                        <>
                          <h3>{uiText.summaryHeaders.actionItems}</h3>
                          <p>{summaryData.summary.actionItems}</p>
                        </>
                      )}
                    </div>
                  )}

                  <h3
                    style={{
                      borderBottom: "1px solid var(--app-border)",
                      paddingBottom: "10px",
                    }}
                  >
                    {uiText.fullTranscript}
                  </h3>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {summaryData.transcript.map((msg, idx) => {
                      let exactSeconds = 0;
                      if (msg.timestamp && summaryData.recordingStartTime) {
                        exactSeconds =
                          (Number(msg.timestamp) * 1000 -
                            summaryData.recordingStartTime) /
                            1000 -
                          EGRESS_BOOT_DELAY;
                      }
                      if (exactSeconds < 0) exactSeconds = 0;

                      const nextMsg = summaryData.transcript[idx + 1];
                      let nextSeconds = 999999;
                      if (
                        nextMsg &&
                        nextMsg.timestamp &&
                        summaryData.recordingStartTime
                      ) {
                        nextSeconds =
                          (Number(nextMsg.timestamp) * 1000 -
                            summaryData.recordingStartTime) /
                            1000 -
                          EGRESS_BOOT_DELAY;
                      }

                      const isActive =
                        !isTextOnly &&
                        currentTime >= exactSeconds &&
                        currentTime < nextSeconds;

                      return (
                        <div
                          key={idx}
                          className={`transcript-item ${isTextOnly ? "" : "clickable"}`}
                          onClick={() =>
                            !isTextOnly && handleSeek(exactSeconds)
                          }
                          style={{
                            background: isActive
                              ? "rgba(99, 102, 241, 0.2)"
                              : msg.isImportant
                                ? "rgba(234, 179, 8, 0.15)"
                                : "rgba(0,0,0,0.2)",
                            borderLeft: isActive
                              ? "3px solid #6366f1"
                              : msg.isImportant
                                ? "3px solid #eab308"
                                : "3px solid transparent",
                            padding: "10px",
                            borderRadius: "6px",
                            fontSize: "14px",
                            transition: "all 0.2s ease-in-out",
                            cursor: isTextOnly ? "default" : "pointer",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "bold",
                              color: isActive
                                ? "#a5b4fc"
                                : msg.isImportant
                                  ? "#fde047"
                                  : "#e5e5e5",
                              marginBottom: "4px",
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{msg.participant}</span>
                            <div>
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: isActive ? "#c7d2fe" : "#888",
                                  marginRight: "8px",
                                }}
                              >
                                {Math.floor(exactSeconds / 60)}:
                                {Math.floor(exactSeconds % 60)
                                  .toString()
                                  .padStart(2, "0")}
                              </span>
                              {msg.isImportant && <span>⭐️</span>}
                            </div>
                          </div>
                          <div
                            style={{
                              color: isActive ? "#fff" : "#ccc",
                              lineHeight: "1.4",
                            }}
                          >
                            {msg.text}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    marginTop: "50px",
                    color: "var(--app-subtle)",
                  }}
                >
                  {uiText.noTextData}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
				@keyframes fadeIn {
					from { opacity: 0; transform: translateX(20px); }
					to { opacity: 1; transform: translateX(0); }
				}
				/* ❗️ ОБНОВЛЕНО: Стили для нового JSON-рендера */
				.summary-container h3 { 
					font-size: 1.1rem; 
					border-bottom: 1px solid rgba(255,255,255,0.1); 
					padding-bottom: 5px; 
					margin-top: 15px; 
					color: var(--app-text);
				}
				.summary-container p {
					color: var(--app-text);
					margin: 10px 0;
				}
				.summary-container li {
					color: var(--app-text);
				}
				
				.transcript-item.clickable:hover {
					background: rgba(255,255,255,0.1) !important;
				}
			`}</style>
    </div>
  );
};
